import { useCallback, useEffect, useState } from 'react';

import {
  Assets,
  Box,
  Button,
  DownloadButton,
  downloadCsv,
  FAQ,
  Flex,
  Headings,
  Line,
  Link,
  Panel,
  Text,
  theme,
  Timeline,
  useMediaMin,
  VerticalSpacer,
} from '@foradevelopment/fora-ui';
import { getReadablePayMonth } from '@fora-monorepo/employer-my-pages-common/utils';
import { ReportingReceiptViewModel } from '@fora-monorepo/employer-my-pages-common/types';
import { FORA_SOURCE } from '@fora-monorepo/fora-common';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';
import { trackCustomEvent } from '@fora-monorepo/webapp-analytics';

import { ReportModal } from '../../components';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { restartReporting } from '../../redux/payroll/payrollSlice';
import { mapPayrollFormDataToPayRowList } from '../utils';
import { getPayMonthOverviews, getPayrollDetailsCSVFromPayrollData } from '../../redux/payroll/payrollThunks';
import { FORA_REPORTER_NAME, KS_ADMIN_ROLE } from '../../common/constants';
import { links } from '../../router/links';
import { formatFileName } from '../../common/utils';
import { useTypedTranslation } from '../../i18n/i18n';

export const ReceiptPanel = () => {
  const { t } = useTypedTranslation();

  const dispatch = useAppDispatch();
  const isDesktop = useMediaMin(theme.breakpoint.lg);

  const user = useAppSelector(state => state.auth.user);
  const { organizationName, organizationNumber } = useAppSelector(state => state.employer.employerInfo);
  const faqs = useAppSelector(state => state.content.content.faqListReportPayrollReceiptStep);
  const reportingPeriod = useAppSelector(state => state.payroll.selectedPayMonthOverview);
  const inputPayData = useAppSelector(state => state.payroll.inputPayData);
  const loadingReportPayroll = useAppSelector(state => state.payroll.loadingReportPayroll);
  const receiptPanelState = useAppSelector(state => state.payroll.receiptPanelState);
  const payAmountSummaryData = useAppSelector(state => state.payroll.payAmountSummaryData);
  const reportDate = useAppSelector(state => state.payroll.reportDate);
  const ffSurveyMultiReporter = useAppSelector(state => state.foraGlobal.settings.ffSurveyMultiReporter);

  const [modalOpen, setModalOpen] = useState(false);

  useEffect(
    () => () => {
      if (loadingReportPayroll === ThunkRequestStatus.Fulfilled) {
        dispatch(getPayMonthOverviews());
      }
    },
    [dispatch, loadingReportPayroll]
  );

  const getReportSummaryData = useCallback(async (): Promise<ReportingReceiptViewModel> => {
    return {
      organizationName,
      organizationNumber,
      payMonthSummaries: [
        {
          payMonth: reportingPeriod?.payMonth || '',
          numberOfPays: payAmountSummaryData.numberOfNewEntries + payAmountSummaryData.numberOfChangedEntries,
          totalSum:
            payAmountSummaryData.totalPayAmountInNewEntries + payAmountSummaryData.totalPayAmountInChangedEntries,
        },
      ],
      reportDate: reportDate || '',
      reporterName: user?.role === KS_ADMIN_ROLE ? FORA_REPORTER_NAME : `${user?.firstName} ${user?.lastName}`,
      source: FORA_SOURCE,
    };
  }, [organizationName, organizationNumber, payAmountSummaryData, reportDate, reportingPeriod?.payMonth, user]);

  const downloadPayrollDetailsCSV = async () => {
    const payroll = mapPayrollFormDataToPayRowList(inputPayData, reportingPeriod?.payMonth);
    const result = await dispatch(
      getPayrollDetailsCSVFromPayrollData({ payRows: payroll, reportedAtTimestamp: reportDate ?? '' })
    );
    if (getPayrollDetailsCSVFromPayrollData.fulfilled.match(result)) {
      if (result?.payload?.base64EncodedFile) {
        downloadCsv(
          result.payload.base64EncodedFile,
          formatFileName({
            fileType: 'report-details',
            createdDate: reportDate ?? ' ',
            employerName: organizationName,
            organizationNumber: organizationNumber,
          })
        );
        return null;
      }
      return t('errorMessages.somethingWentWrongNoFileTryLater');
    } else {
      return t('errorMessages.somethingWentWrongWithCsvTryAgain');
    }
  };

  const handleRestartPayrollInput = () => {
    dispatch(restartReporting());
  };

  const receiptModalButtonText = t('panel.manualReportReceipt.paidPayrolls', {
    payMonth: getReadablePayMonth(reportingPeriod?.payMonth ?? ''),
  });

  return (
    <Panel
      {...receiptPanelState}
      testId="test-panel-receipt"
      previousPanelName="panel-review-confirm"
      name="panel-receipt"
    >
      <Panel.Header>{t('common.receiptTitle')}</Panel.Header>
      <Panel.Body>
        <Panel.MainSection>
          <Assets.CheckmarkIcon color="black90" width={isDesktop ? 30 : 18} />
          <Headings.H2>{t('panel.manualReportReceipt.thankYou')}</Headings.H2>
          <VerticalSpacer marginBottom="2.5rem" />
          <Text fontWeight="light" size="xs" color="steelGrey">
            {t('common.reported')}
          </Text>
          <Text marginBottom={20} fontWeight="light" size="medium">
            {t('panel.manualReportReceipt.paidPayrolls', {
              payMonth: getReadablePayMonth(reportingPeriod?.payMonth ?? ''),
            })}
          </Text>
          <Flex marginBottom={60} flexDirection="column" alignItems="flex-start">
            <Text fontWeight="bold" size="large">
              {t('common.downloadReceiptTitle')}
            </Text>
            <Button
              styleAs="link"
              onClick={() => {
                setModalOpen(true);
                trackCustomEvent('Manual reporting - opened modal', { name: receiptModalButtonText });
              }}
            >
              {receiptModalButtonText}
            </Button>
            <ReportModal
              isOpen={modalOpen}
              closeModal={() => setModalOpen(false)}
              getReportData={getReportSummaryData}
            />
            <DownloadButton
              name={t('list.historyLog.reportedPayrollCsvFileName')}
              download={downloadPayrollDetailsCSV}
            />
          </Flex>
          <Box marginTop="3rem">
            <Timeline title={t('panel.manualReportReceipt.timelineTitle')} currentStep={1}>
              <Timeline.Step
                id="timeline-step-receipt-1"
                text={t('panel.manualReportReceipt.timelineTextStep1')}
                isCompleted={true}
              />
              <Timeline.Step
                id="timeline-step-receipt-2"
                text={t('panel.manualReportReceipt.timelineTextStep2')}
                button={
                  <Button onClick={handleRestartPayrollInput} style={{ marginTop: 5 }} styleAs="link">
                    {t('panel.manualReportReceipt.changeReportedInfo')}
                  </Button>
                }
              />
              <Timeline.Step id="timeline-step-receipt-3" text={t('panel.manualReportReceipt.timelineTextStep3')} />
            </Timeline>
          </Box>
          <VerticalSpacer marginBottom="1.75rem" />
          <Line marginBottom={ffSurveyMultiReporter ? 30 : 16} marginTop="3rem" color="lightGrey" />
          {!!ffSurveyMultiReporter && <Link to="https://www.fora.se/survey-e-service-employer">Lämna ditt omdöme</Link>}
          <Box marginTop={ffSurveyMultiReporter ? 30 : 0}>
            <Link variant="outline" to={links.payroll} styleAs="button" hasIcon={false}>
              {t('panel.fileReportReceipt.viewAllReportsButtonText')}
            </Link>
          </Box>
        </Panel.MainSection>
        <Panel.AsideSection>
          {faqs.length > 0 && isDesktop && <FAQ testId="review-confirm-faq" layout="side" questionAnswers={faqs} />}
        </Panel.AsideSection>
        {faqs.length > 0 && !isDesktop && <FAQ testId="review-confirm-faq" layout="modal" questionAnswers={faqs} />}
      </Panel.Body>
    </Panel>
  );
};
